import React, { useEffect, useMemo, useReducer } from 'react';
import pLimit from 'p-limit';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { SettingsBackupRestoreRounded } from '@mui/icons-material';
import { Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { withStaticSelectEditor } from 'shared/components/data-display/editors';
import { dateString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { formatTenantName } from 'shared/utils/formatters';
import { deviceSearchPath, getTenantPath } from 'shared/routes/tenant';
import {
  deleteRadiusCheck,
  deleteRadiusReply,
  searchTenantRadius,
} from 'shared/api/tenant-radius';
import { buildingEntity, nasEntity, tenantEntity } from 'shared/entityDetails';
import { useRequest } from 'shared/hooks';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import ExportButton from 'shared/components/table/ExportButton';
import PageControls from 'shared/components/table/PageControls';
import useDataTable from 'shared/components/table/useDataTable';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import { InfoModal, Title, ToggleSelect } from 'shared/components/abstract/interactables';
import { getTenantRadiusSearchPath } from 'shared/routes/networking';

// Only make this many delete requests concurrently.
// (this table can potentially display thousands of rows).
const concurrencyLimit = 25;

const columns = [
  {
    name: 'Tenant',
    formatter: (row) => (row.tenant_id
      ? <Link to={getTenantPath(row.tenant_id)}>{formatTenantName(row)}</Link> : ''),
    orderBy: 'first_name',
  },
  {
    name: 'Username',
    formatter: (row) => (/(?:[a-f0-9]{2}:?){5}[a-f0-9]{2}/i.test(row.username) // Regex recognizes mac addresses in either format.
      ? <Link to={`${deviceSearchPath}?q=${row.username}`}>{row.username}</Link>
      : row.username
    ),
    orderBy: 'UserName',
  },
  {
    name: 'Attribute',
    jsonKey: 'attribute',
    orderBy: 'Attribute',
  },
  {
    name: 'Op',
    jsonKey: 'op',
  },
  {
    name: 'Value',
    jsonKey: 'value',
    orderBy: 'Value',
  },
  {
    name: 'Timestamp',
    formatter: (row) => (row.updated_at ? dateString(row.updated_at) : ''),
    orderBy: 'timestamp',
  },
];

const filters = [
  { param: 'username' },
  { param: 'attribute' },
  { param: 'tenant_id' },
  { param: 'nas_id' },
  { param: 'building_id' },
];

const entities = [
  {
    // Special case to render the username input.
    // Rest are for searchable selects.
    name: 'Username',
    param: 'username',
    value: 0,
  },
  {
    Select: withAsyncSelect({
      ...tenantEntity,
      baseSearch: 'include_archived=true',
      defaultOptions: false,
    }),
    name: 'Tenant',
    param: 'tenant_id',
    value: 1,
  },
  {
    Select: withAsyncSelect({
      ...buildingEntity,
      baseSearch: 'include_archived=true',
      defaultOptions: false,
    }),
    name: 'Building',
    param: 'building_id',
    value: 2,
  },
  {
    Select: withAsyncSelect({
      ...nasEntity,
      defaultOptions: false,
    }),
    name: 'Nas',
    param: 'nas_id',
    value: 3,
  },
];

function getInitialState(search) {
  let defaultIndex = 0;
  if (search.includes('tenant')) {
    defaultIndex = 1;
  } else if (search.includes('building')) {
    defaultIndex = 2;
  } else if (search.includes('nas')) {
    defaultIndex = 3;
  }
  return {
    entityIndex: defaultIndex, // Corresponds to an index in entities.
    selectedRows: {},
    updateCount: 0,
    dirtyUsername: '',
  };
}

const AttributeSelect = withStaticSelectEditor([
  'Auth-Type',
  'Cleartext-Password',
  'Framed-Pool',
  'Reply-Message',
  'Tunnel-Medium-Type',
  'Tunnel-Private-Group-Id',
  'Tunnel-Type',
  'User-Password',
]);

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'dirtyUsername':
      return {
        ...state,
        dirtyUsername: payload,
      };
    case 'reload':
      return {
        ...state,
        updateCount: state.updateCount + 1,
      };
    case 'selectedRows':
      return {
        ...state,
        selectedRows: payload,
      };
    case 'entityIndex':
      return {
        ...state,
        entityIndex: payload,
      };
    default:
      return state;
  }
}

export default function TenantRadiusSearch() {
  const { search } = useLocation();
  const { push: pushHistory } = useHistory();
  const { request } = useRequest();

  const [state, dispatch] = useReducer(reducer, getInitialState(search));
  const {
    entityIndex,
    selectedRows,
    updateCount,
    dirtyUsername,
  } = state;

  const {
    clearTable,
    exportCSV,
    isLoading,
    onReorder,
    pageBack,
    pageFirst,
    pageForward,
    pageLast,
    tableState,
  } = useDataTable({
    filters,
    searchRequest: searchTenantRadius,
    showDefaultOptions: false,
    updateCount,
  });

  const {
    availablePages,
    defaultSearch,
    filterState,
    rows,
  } = tableState;

  const {
    order,
    orderBy,
    page,
    username,
  } = filterState;

  // Sync username box to value in url.
  useEffect(() => {
    dispatch({
      action: 'dirtyUsername',
      payload: username || '',
    });
  }, [username]);

  // Remove selection when loaded data changes.
  useEffect(() => {
    dispatch({
      action: 'selectedRows',
      payload: {},
    });
  }, [rows]);

  const submitQuery = (param, value) => {
    const next = { ...filterState };

    // Only set one entity value at a time.
    if (entities.some((e) => e.param === param)) {
      next.username = undefined;
      next.tenant_id = undefined;
      next.building_id = undefined;
      next.nas_id = undefined;
    }
    next[param] = value;
    pushHistory(getTenantRadiusSearchPath(next));
  };

  const deleteSelectedRows = async () => {
    const limit = pLimit(concurrencyLimit);
    const promises = Object
      .values(selectedRows)
      .map((row) => limit(() => (
        row.table === 'radcheck'
          ? request(deleteRadiusCheck(row.id))
          : request(deleteRadiusReply(row.id)))));

    const result = await Promise.all(promises);

    if (result.every((r) => r !== undefined)) {
      dispatch({ action: 'reload' });
    }
  };

  const computedColumns = useMemo(() => {
    const onRowSelect = (row) => {
      const selectedRowsCpy = { ...selectedRows };
      if (selectedRows[row.key]) {
        delete selectedRowsCpy[row.key];
      } else {
        selectedRowsCpy[row.key] = row;
      }
      dispatch({
        action: 'selectedRows',
        payload: selectedRowsCpy,
      });
    };

    const rowSelector = (row) => (
      <input
        type="checkbox"
        value={row.key}
        checked={!!selectedRows[row.key]}
        onChange={() => onRowSelect(row)}
      />
    );

    return [
      ...columns,
      {
        name: 'Select',
        onClick: () => {
          if (Object.values(selectedRows)
            .some((row) => !!row)) {
            dispatch({
              action: 'selectedRows',
              payload: {},
            });
          } else {
            const selectAll = {};
            rows.forEach((row) => {
              selectAll[row.key] = row;
            });
            dispatch({
              action: 'selectedRows',
              payload: selectAll,
            });
          }
        },
        formatter: rowSelector,
      },
    ];
  }, [rows, selectedRows]);
  const {
    Select,
    param: entityParam,
  } = entities[entityIndex];
  const numSelected = Object.keys(selectedRows).length;
  const deleteDescription = `Are you sure you wish to delete ${numSelected} record${numSelected > 1 ? 's' : ''}?`;

  return (
    <>
      <Title title="Tenant Radius Search" />
      <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
        <Grid size={12}>
          <ToggleSelect
            setValue={(value) => dispatch({
              action: 'entityIndex',
              payload: value,
            })}
            value={entityIndex}
            options={entities}
          />
        </Grid>
        <Grid
          container
          size={{ lg: 8, xs: 12 }}
          sx={{ alignItems: 'center' }}
        >
          <Grid size={{ lg: 4, xs: 12 }}>
            {Select
              ? (
                <Select
                  value={filterState[entityParam]}
                  setValue={(value) => submitQuery(entityParam, value)}
                />
              ) : (
                <TextField
                  label="Search Username"
                  className="input-field"
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                  size="small"
                  value={dirtyUsername}
                  onChange={(e) => dispatch({
                    action: 'dirtyUsername',
                    payload: e.target.value,
                  })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitQuery('username', dirtyUsername);
                    }
                  }}
                />
              )}
          </Grid>
          <Grid size={{ lg: 2, xs: 12 }}>
            <AttributeSelect
              label="Attribute"
              setValue={(value) => submitQuery('attribute', value)}
              value={filterState.attribute || ''}
            />
          </Grid>
          <Grid size={{ lg: 2, xs: 12 }}>
            {search && search !== defaultSearch && (
              <Button
                startIcon={<SettingsBackupRestoreRounded />}
                onClick={clearTable}
                variant={VARIANTS.contained}
              >
                Reset Table
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid
          size={{ lg: 2, xs: 12 }}
          sx={{ justifySelf: 'end' }}
        >
          <PageControls
            hidden={availablePages < 2}
            availablePages={availablePages}
            disabled={isLoading}
            page={page}
            pageBack={pageBack}
            pageFirst={pageFirst}
            pageForward={pageForward}
            pageLast={pageLast}
          />
        </Grid>
        <Grid size={12}>
          <StickyHeadTable
            columns={computedColumns}
            currentOrder={order}
            currentOrderBy={orderBy}
            noRowsText="No Results"
            onReorder={onReorder}
            rowId="key"
            rows={rows}
            striped
          />
        </Grid>
        <Grid container size={12} sx={{ justifyContent: 'end' }}>
          <Grid>
            {numSelected > 0 && (
              <InfoModal
                actionCallback={deleteSelectedRows}
                actionName="Delete"
                description={deleteDescription}
                disabled={isLoading}
                title="Confirm Delete"
                color={BUTTON_COLORS.error}
              />
            )}
          </Grid>
          <Grid>
            <ExportButton
              onExport={exportCSV}
              disabled={isLoading}
              hidden={!rows || rows.length === 0}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
